import { RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { IconButton, ICON_SIZE } from '../../design-system';
import { trackEvent } from '../../lib/analytics';
import AIQuestionRow from './AIQuestionRow';

/**
 * SuggestionButtons - Renders clickable follow-up suggestion buttons
 *
 * Used in AI chat messages when enableSuggestions is true.
 * Displays suggestion buttons parsed from reedle:suggestions code blocks.
 * Groups the refresh action with the follow-up heading above the questions.
 *
 * @param {string[]} suggestions - Array of suggestion texts
 * @param {function} onSuggestionClick - Callback when a suggestion is clicked
 * @param {boolean} disabled - When true, buttons are visible but not clickable (e.g., while sending)
 * @param {string} context - 'article_chat' | 'global_chat'
 * @param {function} [onRefresh] - Callback to refresh suggestions
 * @param {boolean} [isRefreshing] - True while refresh is in progress (spins the icon)
 */
export default function SuggestionButtons({
  suggestions,
  onSuggestionClick,
  disabled = false,
  context = 'article_chat',
  onRefresh,
  isRefreshing = false,
}) {
  const { t } = useTranslation('reader');

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const handleClick = (suggestion, index) => {
    if (disabled) return;
    trackEvent('ai_suggestion_clicked', {
      source: 'chat_response',
      context,
      suggestion_text: suggestion,
      suggestion_index: index,
    });
    onSuggestionClick?.(suggestion);
  };

  const handleRefresh = () => {
    if (disabled || isRefreshing) return;
    trackEvent('ai_suggestion_refresh_clicked', { context });
    onRefresh?.();
  };

  return (
    <div className="suggestion-buttons-container">
      <div className="suggestion-buttons-header">
        <span className="suggestion-buttons-label rds-dropdown-menu__label">{t('aiChat.followUpSuggestions')}</span>
        {onRefresh && (
          <IconButton
            className={`suggestion-refresh-btn${isRefreshing ? ' is-refreshing' : ''}`}
            variant="ghost"
            size="sm"
            label={t('aiChat.refreshSuggestions')}
            title={t('aiChat.refreshSuggestions')}
            aria-busy={isRefreshing || undefined}
            onClick={handleRefresh}
            disabled={disabled || isRefreshing}
          >
            <RotateCcw size={ICON_SIZE.xxs} />
          </IconButton>
        )}
      </div>
      <div className="suggestion-buttons">
        {suggestions.map((suggestion, index) => (
          <AIQuestionRow
            key={index}
            className="suggestion-btn"
            question={suggestion}
            onClick={() => handleClick(suggestion, index)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}
